import type { Component } from '../router';
import { ApiError } from '../services/api';
import { postContact, type ContactRequest } from '../api/client';

type SubmitState = 'idle' | 'sending' | 'sent' | 'error';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// errorCopy maps a failed submission to user-facing text. A 400 is a validation
// failure (the client already validates, so this is a fallback), 429 is the
// per-IP rate limit, anything else is a server/transport problem.
export function errorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return 'Please check the form and try again.';
    if (err.status === 429) return 'Too many requests — give it a minute, then try again.';
    return 'Something went wrong on our end. Please try again.';
  }
  return 'Could not reach the server. Check your connection.';
}

// ContactPage is the public contact form. It POSTs JSON to
// /api/contact through the shared transport (which attaches the CSRF header),
// mirroring the contract enforced server-side. No auth required.
export class ContactPage implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const section = el('section', 'contact');
    const card = el('div', 'contact__card');

    const intro = el('div', 'contact__intro');
    intro.append(
      el('h1', 'contact__title', 'Get in touch'),
      el(
        'p',
        'contact__sub',
        'Questions about an order, a product, or anything else? Send us a message and our team will get back to you.',
      ),
    );

    card.append(intro, this.buildForm());
    section.appendChild(card);
    host.appendChild(section);
    this.root = section;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }

  private buildForm(): HTMLElement {
    const form = el('form', 'contact__form');
    form.noValidate = true;

    const name = field('text', 'name', 'Name', { required: true, autocomplete: 'name', maxLength: 120 });
    const email = field('email', 'email', 'Email', {
      required: true,
      autocomplete: 'email',
      maxLength: 254,
      placeholder: 'name@example.com',
    });
    const phone = field('tel', 'phone', 'Phone (optional)', { autocomplete: 'tel', maxLength: 40 });
    const subject = field('text', 'subject', 'Subject', { required: true, maxLength: 160 });
    const message = textareaField('message', 'Message', { required: true, maxLength: 4000 });

    // Honeypot: visually hidden, off the tab order, never autofilled. A bot that
    // fills it is silently dropped server-side.
    const honeypot = el('div', 'contact__hp');
    honeypot.setAttribute('aria-hidden', 'true');
    const hpInput = el('input');
    hpInput.type = 'text';
    hpInput.name = 'company';
    hpInput.tabIndex = -1;
    hpInput.autocomplete = 'off';
    const hpLabel = el('label', undefined, 'Company');
    hpLabel.htmlFor = 'contact-company';
    hpInput.id = 'contact-company';
    honeypot.append(hpLabel, hpInput);

    // Consent checkbox.
    const consentWrap = el('label', 'contact__consent');
    const consent = el('input', 'contact__consent-box');
    consent.type = 'checkbox';
    consent.name = 'consent';
    consent.required = true;
    consentWrap.append(
      consent,
      el('span', 'contact__consent-text', 'I consent to Asmaluz contacting me about this enquiry.'),
    );

    const submit = el('button', 'contact__submit');
    submit.type = 'submit';
    submit.dataset.state = 'idle';
    submit.appendChild(el('span', 'contact__submit-label', 'Send message'));

    const status = el('p', 'contact__status');
    status.dataset.visible = 'false';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const setState = (next: SubmitState, message?: string, tone?: 'success' | 'error'): void => {
      submit.dataset.state = next;
      submit.disabled = next === 'sending';
      if (message) {
        status.textContent = message;
        status.dataset.visible = 'true';
        if (tone) status.dataset.tone = tone;
        else delete status.dataset.tone;
      } else {
        status.dataset.visible = 'false';
      }
    };

    form.addEventListener('input', () => {
      if (submit.dataset.state === 'error') setState('idle');
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (submit.disabled) return;

      if (!form.checkValidity()) {
        setState('error', 'Please complete the required fields.', 'error');
        form.reportValidity();
        return;
      }

      const body: ContactRequest = {
        name: name.input.value.trim(),
        email: email.input.value.trim(),
        phone: phone.input.value.trim() || undefined,
        subject: subject.input.value.trim(),
        message: message.input.value.trim(),
        company: hpInput.value.trim() || undefined,
        consent: consent.checked,
      };

      setState('sending', 'Sending…');
      try {
        await postContact(body);
        this.showConfirmation(form.parentElement as HTMLElement);
      } catch (err) {
        setState('error', errorCopy(err), 'error');
      }
    });

    form.append(
      name.field,
      email.field,
      phone.field,
      subject.field,
      message.field,
      honeypot,
      consentWrap,
      submit,
      status,
    );

    queueMicrotask(() => name.input.focus());
    return form;
  }

  // showConfirmation swaps the form for a success view in place.
  private showConfirmation(card: HTMLElement): void {
    const form = card.querySelector('.contact__form');
    form?.remove();
    const box = el('div', 'contact__success');
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    box.append(
      el('h2', 'contact__success-title', 'Message sent'),
      el(
        'p',
        'contact__success-lead',
        'Thanks for reaching out. A member of our team will be in touch shortly.',
      ),
    );
    card.appendChild(box);
  }
}

type FieldOpts = {
  required?: boolean;
  autocomplete?: string;
  maxLength?: number;
  placeholder?: string;
};

function field(
  type: string,
  nameAttr: string,
  labelText: string,
  opts: FieldOpts = {},
): { field: HTMLElement; input: HTMLInputElement } {
  const wrap = el('div', 'contact__field');
  const label = el('label', 'contact__label', labelText);
  label.htmlFor = `contact-${nameAttr}`;
  const input = el('input', 'contact__input');
  input.id = `contact-${nameAttr}`;
  input.name = nameAttr;
  input.type = type;
  if (opts.required) input.required = true;
  if (opts.autocomplete) input.autocomplete = opts.autocomplete as AutoFill;
  if (opts.maxLength) input.maxLength = opts.maxLength;
  if (opts.placeholder) input.placeholder = opts.placeholder;
  wrap.append(label, input);
  return { field: wrap, input };
}

function textareaField(
  nameAttr: string,
  labelText: string,
  opts: FieldOpts = {},
): { field: HTMLElement; input: HTMLTextAreaElement } {
  const wrap = el('div', 'contact__field');
  const label = el('label', 'contact__label', labelText);
  label.htmlFor = `contact-${nameAttr}`;
  const input = el('textarea', 'contact__input contact__textarea');
  input.id = `contact-${nameAttr}`;
  input.name = nameAttr;
  input.rows = 6;
  if (opts.required) input.required = true;
  if (opts.maxLength) input.maxLength = opts.maxLength;
  wrap.append(label, input);
  return { field: wrap, input };
}
